import { defineStore } from "pinia";
import { useConfigStore } from "./config";
import { useUIStore } from "./ui";
import axios from "axios";

import cornerstoneDICOMImageLoader from "@cornerstonejs/dicom-image-loader";
import * as cornerstoneTools from "@cornerstonejs/tools";

import { useToolsStore } from "./tools";

// composables
import { compareDates } from "../composables/PureUtils";
import { formatDicomDate } from "../composables/DicomUtils";
import getNumberOfPriors from "../composables/GetNumberOfPriors";

// classes
import Layout from "../classes/Layout";
import { useAlertStore } from "./alert";

export const useMainStore = defineStore("main", {
  state: () => ({
    type: "",
    studiesLoaded: [],
    imagesLoaded: false,
    preDataLoaded: false,
    selectedCanvas: -1,
    totalCanvases: -1,
    MainPgSelectedCanvasIdx: 0, //index counts from 0
    currentStudy: "",
    currentSeries: "",
    mprMode: false,
    mprStudyUID: "",
    mprSeriesUID: "",
    vrMode: false,
    selectedSeries: 1,
    selectedSeriesElement: null,
    selectedStudy: 1,
    studyBefore3D: null,
    cachedSeriesMetadata: [],
    currentLayout: "1x1",
    layout: new Layout({}),
    prevLayout: "",
    fromSeries: 1,
    toSeries: 1,
    focusSeries: false,
    focusedStudyUID: "",
    focusedSeriesUID: "",
    firstCanvasLoad: true,
    canvases: new Array(32),
    layoutBefore3D: "",
    relatedStudies: [],
    seriesFPS: 0,
    cinedata: [],
    currentEchoView: "",
    currentEchoStage: "",
    echoSeries: [],
    autoStartEcho: true,
    byView: true,
    isEchoLayout: false,
    checkForStressEcho: false,
    selectedEchoIndex: 0,
    syncStacks: false,
    cacheImages: true,
    allThumbnailsLoaded: false,
    cachedSeriesUIDs: [],
    mainSeriesUIDs: [],
    priorSeriesUIDs: [],
    imageIdsCache: [],
    VRLastStudyUID: "",
    VRLastSeriesUID: "",
    VRImageIdsCache: [],
    VRVolumeCache: null,
    multiSelectedViewports: [],
    multiSelectedViewportCache: [],
    defaults: [],
    currentPreset: "",
    thumbnailDefaults: [],
    multiSeriesScrolling: false,
    scrollingCanvasId: [],
    layoutChangeCounter: 0,
    sliders: [
      { name: "Ambient", min: 0, max: 1, step: 0.1, value: 0 },
      { name: "Diffuse", min: 0, max: 1, step: 0.1, value: 0 },
      { name: "Specular", min: 0, max: 1, step: 0.1, value: 0 },
      { name: "Specular Power", min: 0, max: 1, step: 0.1, value: 0 },
      { name: "Opacity Unit Distance", min: 0, max: 40, step: 0.01, value: 2 },
    ],
    checkboxes: [
      //{ name: "Use LabelOutline", checked: false },
      { name: "Shade", checked: false },
      { name: "Show Crop Box", checked: true },
      { name: "Show Clipping Planes", checked: true },
    ],

    nonImagingSopClasses: new Map([
      ["1.2.840.10008.5.1.4.1.1.481.2", "RTDose"],
      ["1.2.840.10008.5.1.4.1.1.481.3", "RTStructureSet"],
      ["1.2.840.10008.5.1.4.1.1.481.5", "RTPlan"],
      ["1.2.840.10008.5.1.4.1.1.481.4", "RTBeamsTreatmentRecord"],
      ["1.2.840.10008.5.1.4.1.1.481.6", "RTBrachyTreatmentRecord"],
      ["1.2.840.10008.5.1.4.1.1.481.7", "RTTreatmentSummaryRecord"],
      ["1.2.840.10008.5.1.4.1.1.77.1.1.1", "VideoEndoscopic"],
      ["1.2.840.10008.5.1.4.1.1.77.1.4.1", "VideoPhotographic"],
      ["1.2.840.10008.5.1.4.1.1.11.1", "GrayscaleSoftcopyPresentationState"],
      ["1.2.840.10008.5.1.4.1.1.11.2", "ColorSoftcopyPresentationState"],
      ["1.2.840.10008.5.1.4.1.1.11.3", "PseudoColorSoftcopyPresentationState"],
      ["1.2.840.10008.5.1.4.1.1.11.4", "BlendingSoftcopyPresentationState"],
      ["1.2.840.10008.5.1.4.1.1.131", "BasicStructuredDisplay"],
      [
        "1.2.840.10008.5.1.4.1.1.11.5",
        "XA_XRFGrayscaleSoftcopyPresentationState",
      ],
      [
        "1.2.840.10008.5.1.4.1.1.11.8",
        "AdvancedBlendingPresentationStateStorage",
      ],
      [
        "1.2.840.10008.5.1.4.1.1.11.12",
        "VariableModalityLUTSoftcopyPresentationStateStorage",
      ],
      ["1.2.840.10008.5.1.4.1.1.9.4.1", "BasicVoiceAudioWaveform"],
      ["1.2.840.10008.5.1.4.1.1.9.1.1", "TwelveLeadECGWaveform"],
      ["1.2.840.10008.5.1.4.1.1.9.1.2", "GeneralECGWaveform"],
      ["1.2.840.10008.5.1.4.1.1.9.1.3", "AmbulatoryECGWaveform"],
      ["1.2.840.10008.5.1.4.1.1.9.2.1", "HemodynamicWaveform"],
      ["1.2.840.10008.5.1.4.1.1.9.3.1", "CardiacElectrophysiologyWaveform"],
      ["1.2.840.10008.5.1.4.1.1.9.5.1", "ArterialPulseWaveform"],
      ["1.2.840.10008.5.1.4.1.1.9.6.1", "RespiratoryWaveform"],
      ["1.2.840.10008.5.1.4.1.1.9.4.2", "GeneralAudioWaveform"],
      [
        "1.2.840.10008.5.1.4.1.1.9.7.1",
        "RoutineScalpElectroencephalogramWaveformStorage",
      ],
      ["1.2.840.10008.5.1.4.1.1.9.7.2", "ElectromyogramWaveformStorage"],
      ["1.2.840.10008.5.1.4.1.1.9.7.3", "ElectrooculogramWaveformStorage"],
      [
        "1.2.840.10008.5.1.4.1.1.9.7.4",
        "SleepElectroencephalogramWaveformStorage",
      ],
      [
        "1.2.840.10008.5.1.4.1.1.9.6.2",
        "MultichannelRespiratoryWaveformStorage",
      ],
      ["1.2.840.10008.5.1.4.1.1.9.8.1", "BodyPositionWaveformStorage"],
      ["1.2.840.10008.5.1.4.1.1.9.1.4", "General32bitECGWaveformStorage"],
      ["1.2.840.10008.5.1.4.1.1.88.11", "BasicTextSR"],
      ["1.2.840.10008.5.1.4.1.1.88.22", "EnhancedSR"],
      ["1.2.840.10008.5.1.4.1.1.88.33", "ComprehensiveSR"],
      ["1.2.840.10008.5.1.4.1.1.88.59", "KeyObjectSelectionDocument"],
      ["1.2.840.10008.5.1.4.1.1.88.50", "MammographyCADSR"],
      ["1.2.840.10008.5.1.4.1.1.88.65", "ChestCADSR"],
      ["1.2.840.10008.5.1.4.1.1.88.40", "ProcedureLog"],
      ["1.2.840.10008.5.1.4.1.1.88.67", "XRayRadiationDoseSR"],
      ["1.2.840.10008.5.1.4.1.1.78.6", "SpectaclePrescriptionReport"],
      ["1.2.840.10008.5.1.4.1.1.88.69", "ColonCADSR"],
      ["1.2.840.10008.5.1.4.1.1.79.1", "MacularGridThicknessandVolumeReport"],
      ["1.2.840.10008.5.1.4.1.1.88.70", "ImplantationPlanSR"],
      ["1.2.840.10008.5.1.4.1.1.88.34", "Comprehensive3DSRStorage"],
      [
        "1.2.840.10008.5.1.4.1.1.88.68",
        "RadiopharmaceuticalRadiationDoseSRStorage",
      ],
      ["1.2.840.10008.5.1.4.1.1.88.35", "ExtensibleSRStorage"],
      ["1.2.840.10008.5.1.4.1.1.88.71", "AcquisitionContextSRStorage"],
      ["1.2.840.10008.5.1.4.1.1.88.72", "SimplifiedAdultEchoSRStorage"],
      ["1.2.840.10008.5.1.4.1.1.88.73", "PatientRadiationDoseSRStorage"],
      [
        "1.2.840.10008.5.1.4.1.1.88.74",
        "PlannedImagingAgentAdministrationSRStorage",
      ],
      [
        "1.2.840.10008.5.1.4.1.1.88.75",
        "PerformedImagingAgentAdministrationSRStorage",
      ],
      ["1.2.840.10008.5.1.4.1.1.88.76", "EnhancedXRayRadiationDoseSRStorage"],
      ["1.2.840.10008.5.1.4.1.1.88.77", "WaveformAnnotationSRStorage"],
      ["1.2.840.10008.5.1.4.1.1.4.2", "MRSpectroscopy"],
      ["1.2.840.10008.5.1.4.1.1.66", "RawData"],
      ["1.2.840.10008.5.1.4.1.1.66.1", "SpatialRegistration"],
      ["1.2.840.10008.5.1.4.1.1.66.3", "DeformableSpatialRegistration"],
      ["1.2.840.10008.5.1.4.1.1.66.2", "SpatialFiducials"],
      ["1.2.840.10008.5.1.4.38.1", "HangingProtocol"],
      ["1.2.840.10008.5.1.4.1.1.104.1", "EncapsulatedPDF"],
      ["1.2.840.10008.5.1.4.1.1.104.2", "EncapsulatedCDA"],
      ["1.2.840.10008.5.1.4.1.1.66.5", "SurfaceSegmentation"],
      ["1.2.840.10008.5.1.4.39.1", "ColorPalette"],
      ["1.2.840.10008.5.1.4.1.1.78.1", "LensometryMeasurements"],
      ["1.2.840.10008.5.1.4.1.1.78.2", "AutorefractionMeasurements"],
      ["1.2.840.10008.5.1.4.1.1.78.3", "KeratometryMeasurements"],
      ["1.2.840.10008.5.1.4.1.1.78.4", "SubjectiveRefractionMeasurements"],
      ["1.2.840.10008.5.1.4.1.1.78.5", "VisualAcuityMeasurements"],
      ["1.2.840.10008.5.1.4.1.1.78.7", "OphthalmicAxialMeasurements"],
      ["1.2.840.10008.5.1.4.1.1.78.8", "IntraocularLensCalculations"],
      ["1.2.840.10008.5.1.4.43.1", "GenericImplantTemplate"],
      ["1.2.840.10008.5.1.4.44.1", "ImplantAssemblyTemplate"],
      ["1.2.840.10008.5.1.4.45.1", "ImplantTemplateGroup"],
      ["1.2.840.10008.5.1.4.34.7", "RTBeamsDeliveryInstruction"],
      ["1.2.840.10008.5.1.4.1.1.68.1", "SurfaceScanMeshStorage"],
      ["1.2.840.10008.5.1.4.1.1.68.2", "SurfaceScanPointCloudStorage"],
      ["1.2.840.10008.5.1.4.1.1.66.6", "TractographyResultsStorage"],
      [
        "1.2.840.10008.5.1.4.34.10",
        "RTBrachyApplicationSetupDeliveryInstructionStorage",
      ],
      [
        "1.2.840.10008.5.1.4.1.1.11.6",
        "GrayscalePlanarMPRVolumetricPresentationStateStorage",
      ],
      [
        "1.2.840.10008.5.1.4.1.1.11.9",
        "VolumeRenderingVolumetricPresentationStateStorage",
      ],
      ["1.2.840.10008.5.1.4.1.1.90.1", "ContentAssessmentResultsStorage"],
      ["1.2.840.10008.5.1.4.1.1.200.2", "CTPerformedProcedureProtocolStorage"],
      ["1.2.840.10008.5.1.4.1.1.200.1", "CTDefinedProcedureProtocolStorage"],
      ["1.2.840.10008.5.1.4.1.1.200.3", "ProtocolApprovalStorage"],
      ["1.2.840.10008.5.1.4.1.1.200.8", "XAPerformedProcedureProtocolStorage"],
      ["1.2.840.10008.5.1.4.1.1.200.7", "XADefinedProcedureProtocolStorage"],
      ["1.2.840.10008.5.1.4.1.1.104.3", "EncapsulatedSTL"],
      ["1.2.840.10008.5.1.4.1.1.104.4", "EncapsulatedOBJStorage"],
      ["1.2.840.10008.5.1.4.1.1.104.5", "EncapsulatedMTLStorage"],
      ["1.2.840.10008.5.1.4.1.1.481.10", "RTPhysicianIntentStorage"],
      ["1.2.840.10008.5.1.4.1.1.481.11", "RTSegmentAnnotationStorage"],
      ["1.2.840.10008.5.1.4.1.1.481.12", "RTRadiationSetStorage"],
      ["1.2.840.10008.5.1.4.1.1.481.13", "CArmPhotonElectronRadiationStorage"],
      ["1.2.840.10008.5.1.4.1.1.481.14", "TomotherapeuticRadiationStorage"],
      ["1.2.840.10008.5.1.4.1.1.481.15", "RoboticArmRadiationStorage"],
      ["1.2.840.10008.5.1.4.1.1.481.16", "RTRadiationRecordSetStorage"],
      ["1.2.840.10008.5.1.4.1.1.481.17", "RTRadiationSalvageRecordStorage"],
      [
        "1.2.840.10008.5.1.4.1.1.481.19",
        "CArmPhotonElectronRadiationRecordStorage",
      ],
      [
        "1.2.840.10008.5.1.4.1.1.481.18",
        "TomotherapeuticRadiationRecordStorage",
      ],
      ["1.2.840.10008.5.1.4.1.1.481.20", "RoboticRadiationRecordStorage"],
      [
        "1.2.840.10008.5.1.4.1.1.481.21",
        "RTRadiationSetDeliveryInstructionStorage",
      ],
      ["1.2.840.10008.5.1.4.1.1.481.22", "RTTreatmentPreparationStorage"],
      [
        "1.2.840.10008.5.1.4.1.1.481.25",
        "RTPatientPositionAcquisitionInstructionStorage",
      ],
      [
        "1.2.840.10008.5.1.4.1.1.91.1",
        "MicroscopyBulkSimpleAnnotationsStorage",
      ],
      ["1.2.840.10008.5.1.4.1.1.201.1", "InventoryStorage"],
      ["1.2.840.10008.5.1.4.1.1.66.8", "HeightMapSegmentationStorage"],
      ["1.2.840.10008.5.1.4.1.1.9.100.1", "WaveformPresentationStateStorage"],
      [
        "1.2.840.10008.5.1.4.1.1.9.100.2",
        "WaveformAcquisitionPresentationStateStorage",
      ],
      ["1.2.840.10008.5.1.4.1.1.201.5", "InventoryCreation"],
    ]),
  }),
  getters: {
    getSortedSeries: (state) => (studyUID) => {
      const study = state.studiesLoaded.find((x) => x.studyUID === studyUID);
      if (!study) return [];
      return [...study.series].sort(
        (a, b) => Number(a.seriesNumber) - Number(b.seriesNumber),
      );
    },
  },
  actions: {
    /**
     * Scroll selected series in given direction
     *
     * If scrolling up increment series number to next available
     *
     * If scrolling down decrement series number to next available
     *
     * @param {Boolean} up
     */
    handleSeriesScroll(up) {
      const alert = useAlertStore();

      const isMultiSelect = this.multiSelectedViewports.length > 0;
      let visibleMultiSelected =
        document.getElementsByClassName("multi-selected");

      if (isMultiSelect && visibleMultiSelected.length != 0) {
        let count = visibleMultiSelected.length;
        for (let i = 0; i < count; i++) {
          const viewportElement = visibleMultiSelected[i].querySelector(
            "[data-viewport-uid]",
          );
          this.SeriesScrollHelper(viewportElement, up, true);
        }
      } else if (this.selectedSeriesElement) {
        let elem = this.selectedSeriesElement.parentElement.parentElement;
        this.SeriesScrollHelper(elem, up, false);
      } else if (this.focusSeries && this.layout.toString() === "1x1") {
        if (this.scrollingCanvasId.length > 0) {
          alert.showNeutralAlert({
            text: "A series is currently loading, please wait...",
          });
          return;
        }
        if (this.focusedSeriesUID && this.focusedStudyUID) {
          this.SeriesScrollHelper(null, up, false);
          return;
        }
        alert.showFailureAlert({
          text: "A series must be selected to series scroll",
        });
      } else {
        if (this.scrollingCanvasId.length > 0) {
          alert.showNeutralAlert({
            text: "A series is currently loading, please wait...",
          });
        } else {
          alert.showFailureAlert({
            text: "A series must be selected to series scroll",
          });
        }
      }
    },

    updateFocusedSeries(studyUID, seriesUID) {
      this.layoutChangeCounter++;
      this.scrollingCanvasId = [];
      this.focusedStudyUID = studyUID;
      this.focusedSeriesUID = seriesUID;
    },

    /**
     * Handle actual incrementation or decrementation of displayed series number and re render canvas
     *
     * @param {*} elem
     * @param {*} up
     * @param {*} multi
     * @returns
     */
    SeriesScrollHelper(elem, up, multi = false) {
      const alert = useAlertStore();
      if (this.focusSeries && this.layout.toString() === "1x1") {
        const studyUID = this.focusedStudyUID;
        const study = this.studiesLoaded.find((x) => x.studyUID === studyUID);

        if (!study) {
          alert.showFailureAlert({ text: "Study not found" });
          return;
        }

        const allSeriesSorted = this.getSortedSeries(studyUID);
        const currentIndex = allSeriesSorted.findIndex(
          (x) => x.seriesUID === this.focusedSeriesUID,
        );

        if (currentIndex === -1) {
          alert.showFailureAlert({ text: "Current series not found" });
          return;
        }

        // Boundary checks
        if (currentIndex === 0 && !up) {
          alert.showNeutralAlert({
            text: "Already at the first series in this study",
          });
          return;
        }

        if (currentIndex === allSeriesSorted.length - 1 && up) {
          alert.showNeutralAlert({
            text: "Already at the last series in this study",
          });
          return;
        }

        const nextIndex = up ? currentIndex + 1 : currentIndex - 1;
        const nextSeriesUID = allSeriesSorted[nextIndex].seriesUID;
        this.updateFocusedSeries(studyUID, nextSeriesUID);
        return;
      }
      if (!elem) {
        alert.showFailureAlert({ text: "Failed to retrieve selected element" });
        return;
      }

      let id;
      try {
        id = elem.attributes["data-viewport-uid"].value;
      } catch (err) {
        return;
      }

      const canvasId = parseInt(id.split("_")[1]);
      const canvas = this.canvases.find((x) => x.id == canvasId);

      if (!canvas) {
        alert.showFailureAlert({ text: "Failed to retrieve selected canvas" });
        return;
      }

      const studyUID = canvas.studyUID;
      const study = this.studiesLoaded.find((x) => x.studyUID === studyUID);

      if (!study) return;

      const series = study.series.find((x) => x.seriesUID == canvas.seriesUID);
      if (!series) return;

      const allSeriesSorted = this.getSortedSeries(studyUID);

      const selectedSeriesIndex = allSeriesSorted.findIndex(
        (x) => x.seriesUID === series.seriesUID,
      );

      // Boundary checks
      if (selectedSeriesIndex === 0 && !up) {
        alert.showNeutralAlert({
          text: "Already at the first series in this study",
        });
        return;
      }

      if (selectedSeriesIndex === allSeriesSorted.length - 1 && up) {
        alert.showNeutralAlert({
          text: "Already at the last series in this study",
        });
        return;
      }

      const nextIndex = up ? selectedSeriesIndex + 1 : selectedSeriesIndex - 1;
      const nextSeriesUID = allSeriesSorted[nextIndex].seriesUID;

      if (multi) this.multiSeriesScrolling = true;

      this.handleChangeSeriesSpecific(studyUID, nextSeriesUID, canvasId);
    },
    /**
     * Display specified series in canvas with provided canvas id
     *
     * @param {*} studyUID
     * @param {*} seriesUID
     * @param {*} canvasId
     */
    /**
     * Display specified series in canvas with provided canvas id
     */
    handleChangeSeriesSpecific(studyUID, seriesUID, canvasId) {
      const study = this.studiesLoaded.find((x) => x.studyUID === studyUID);
      const series = study
        ? study.series.find((x) => x.seriesUID === seriesUID)
        : null;

      if (!study || !series) return;

      this.unselectSeries();

      if (!this.scrollingCanvasId.includes(canvasId)) {
        this.scrollingCanvasId.push(canvasId);

        setTimeout(() => {
          const index = this.scrollingCanvasId.indexOf(canvasId);
          if (index > -1) {
            console.warn(`Force clearing busy state for canvas ${canvasId}`);
            this.scrollingCanvasId.splice(index, 1);
          }
        }, 2000);
      }

      const modality = series.modality || "";
      const sopClassUID = series.instanceData?.[0]?.sopClassUID || "";

      this.canvases[canvasId - 1] = {
        id: canvasId,
        type: "STACK",
        seriesUID: seriesUID,
        studyUID: studyUID,
        imageIds: [],
        modality: modality,
        firstInstanceSOPClassUID: sopClassUID,
      };
    },

    /**
     * Handle toggling between focused and unfocused status. When a series is focused it will be displayed on its own
     * in a 1x1 grid
     *
     * @param {*} studyUID
     * @param {*} seriesUID
     */
    toggleFocusedSeries(studyUID, seriesUID) {
      const ui = useUIStore();
      this.layoutChangeCounter++;
      this.scrollingCanvasId = [];

      if (this.focusSeries && this.layout.toString() == "1x1") {
        const { ToolGroupManager } = cornerstoneTools;
        var toolGroup = ToolGroupManager.getToolGroup("STACK_TOOLS");
        if (toolGroup?.toolOptions.ScaleOverlay?.mode != "Disabled") {
          toolGroup.setToolDisabled("ScaleOverlay");
        }

        if (
          this.focusedSeriesUID != seriesUID ||
          this.focusedStudyUID != studyUID
        ) {
          this.focusedStudyUID = studyUID;
          this.focusedSeriesUID = seriesUID;
        } else {
          const originalIdx = this.MainPgSelectedCanvasIdx;
          this.canvases[originalIdx] = {
            ...this.canvases[originalIdx],
            seriesUID: this.focusedSeriesUID,
            studyUID: this.focusedStudyUID,
          };

          this.layout.setLayoutFromString(this.prevLayout);
          this.focusSeries = false;
        }
      } else {
        this.prevLayout = this.layout.toString();
        this.MainPgSelectedCanvasIdx = this.selectedCanvas;

        this.focusedStudyUID = studyUID;
        this.focusedSeriesUID = seriesUID;
        this.layout.setLayoutFromString("1x1");
        this.focusSeries = true;
      }

      ui.SetLayoutRowCol(this.layout);
    },

    unselectSeries() {
      this.selectedCanvas = "";
      this.selectedSeriesElement = "";
      this.selectedSeries = 1;
    },
    firstCanvasLoadDone() {
      this.firstCanvasLoad = false;
    },
    AddImageIdsToChache(studyUID, seriesUID, imageIds) {
      var temp = {
        studyUID: studyUID,
        seriesUID: seriesUID,
        imageIds: imageIds,
      };

      var record = this.imageIdsCache.filter(
        (x) => x.studyUID == studyUID && x.seriesUID == seriesUID,
      );
      if (!record.length > 0) this.imageIdsCache.push(temp);
    },
    CinePlay(viewport) {
      var toolsStore = useToolsStore();
      var imageIds = viewport.imageIds;
      var currentImageId = viewport.getCurrentImageId();
      var seriesUID = currentImageId.substring(
        currentImageId.indexOf("series") + 7,
        currentImageId.indexOf("/instances"),
      );
      if (this.cinedata.filter((x) => x.seriesUID == seriesUID).length > 0) {
        this.CineSoftPlay();
      } else {
        var cwi = cornerstoneDICOMImageLoader;
        var metadata = cwi.wadors.metaDataManager.get(
          imageIds[viewport.getTargetImageIdIndex()],
        );
        var numberOfFrames = metadata["00280008"]
          ? metadata["00280008"].Value[0]
          : 1;
        //make multiframe stack
        var multiFrameStack = [];
        for (let index = 0; index < numberOfFrames; index++) {
          var frameId = `${currentImageId.split("frames/")[0]}frames/${
            index + 1
          }`;
          multiFrameStack.push(frameId);
        }
        viewport.setStack(multiFrameStack, 0);
        toolsStore.PlaySeries(); //act as if it is a series

        var temp = {
          seriesUID: seriesUID,
          playing: true,
        };
        this.cinedata.push(temp);
      }
    },
    CineSoftPlay(viewport) {
      var toolsStore = useToolsStore();
      toolsStore.PlaySeries();
    },
    CineSoftStop(viewport) {
      var toolsStore = useToolsStore();
      toolsStore.StopSeries();
    },
    CineStop(viewport) {
      const cwi = cornerstoneDICOMImageLoader;
      const metadata = cwi.wadors.metaDataManager.get(
        viewport.imageIds[viewport.getTargetImageIdIndex()],
      );
      const seriesUID = metadata["0020000E"]?.Value?.[0];

      const toolsStore = useToolsStore();
      toolsStore.StopSeries();

      const index = this.cinedata.findIndex((x) => x.seriesUID === seriesUID);
      if (index !== -1) {
        this.cinedata.splice(index, 1);
      }
    },
    SetLayout(layout) {
      this.layout.setLayoutFromString(layout);
    },
    CurrentStudy() {
      return (
        this.studiesLoaded.find((x) => x.studyUID === this.currentStudy) || null
      );
    },
    GetImageIds(studyUID, seriesUID) {
      const match = this.cachedSeriesMetadata.find(
        (x) => x.studyUID === studyUID && x.seriesUID === seriesUID,
      );
      return match?.imageIds || [];
    },
    SelectCanvas(index) {
      this.selectedCanvas = index;
    },
    SelectSeries(study, series, element) {
      this.currentStudy = study;
      this.currentSeries = series.seriesUID;
      this.selectedSeriesElement = element;
      this.selectedSeries = series;

      const toolsStore = useToolsStore();
      const config = useConfigStore();

      if (config.refLinesEnabled) {
        toolsStore.ChangeRefLines();
      }
    },
    MultiPart_parse(body, contentType) {
      // Examples for content types:
      //      multipart/form-data; boundary="----7dd322351017c"; ...
      //      multipart/form-data; boundary=----7dd322351017c; ...
      var m = contentType.match(/boundary=(?:"([^"]+)"|([^;]+))/i);
      var s;
      var fieldName = "jpeg";
      if (!m) {
        throw new Error("Bad content-type header, no multipart boundary");
      }

      var boundary = m[1] || m[2];

      function Header_parse(header) {
        var headerFields = {};
        var matchResult = header.match(/^.*name="([^"]*)"$/);
        if (matchResult) headerFields.name = matchResult[1];
        return headerFields;
      }

      function rawStringToBuffer(str) {
        var idx,
          len = str.length,
          arr = new Array(len);
        for (idx = 0; idx < len; ++idx) {
          arr[idx] = str.charCodeAt(idx) & 0xff;
        }
        return new Uint8Array(arr).buffer;
      }

      // \r\n is part of the boundary.
      boundary = "\r\n--" + boundary;

      var isRaw = typeof body !== "string";

      if (isRaw) {
        var view = new Uint8Array(body);
        s = String.fromCharCode.apply(null, view);
      } else {
        s = body;
      }

      // Prepend what has been stripped by the body parsing mechanism.
      s = "\r\n" + s;

      var parts = s.split(new RegExp(boundary)),
        partsByName = {};

      // First part is a preamble, last part is closing '--'
      for (var i = 1; i < parts.length - 1; i++) {
        var subparts = parts[i].split("\r\n\r\n");
        var headers = subparts[0].split("\r\n");
        for (var j = 1; j < headers.length; j++) {
          var headerFields = Header_parse(headers[j]);
          if (headerFields.name) {
            fieldName = headerFields.name;
          }
        }

        partsByName[fieldName] = isRaw
          ? rawStringToBuffer(subparts[1])
          : subparts[1];
      }

      return partsByName;
    },
    hexToBase64(str) {
      // var bString = "";
      // for (var i = 0; i < str.length; i += 2) {
      //    bString += String.fromCharCode(parseInt(str.substr(i, 2), 16));
      // }
      // return btoa(unescape(encodeURIComponent(bString)));
      var hexArray = str
        .replace(/\r|\n/g, "")
        .replace(/([\da-fA-F]{2}) ?/g, "0x$1 ")
        .replace(/ +$/, "")
        .split(" ");
      var byteString = String.fromCharCode.apply(null, hexArray);

      return window.btoa(byteString);
    },
    getPdfData(data) {
      var contentType = data.headers["content-type"].match(
        /boundary=(?:"([^"]+)"|([^;]+))/i,
      );
      let contentTypeParts = contentType.split(";");
      let boundary = contentTypeParts[1].split("=")[1];
      let parts = data.data.message
        .split(boundary)
        .filter((part) => part !== "--" && part !== "");
      let parsedParts = parts[1].split("\n").filter((part) => part !== "");
      let shifted = parsedParts.splice(4, parsedParts.length - 2);
      return shifted.join();
    },
    async instanceLevelQido(series, studyUID) {
      const config = useConfigStore();
      var instancesQido = `${config.qidoRoot}/studies/${studyUID}/series/${series.seriesUID}/instances?includefield=00080018&includefield=00080016`;
      var resp = await axios.get(instancesQido);
      for (let index = 0; index < resp.data.length; index++) {
        const instanceUID = resp.data[index]["00080018"]
          ? resp.data[index]["00080018"].Value
            ? resp.data[index]["00080018"].Value[0]
            : ""
          : "";

        const sopClassUID = resp.data[index]["00080016"]
          ? resp.data[index]["00080016"].Value
            ? resp.data[index]["00080016"].Value[0]
            : ""
          : "";
        const instance = {
          instanceUID,
          sopClassUID,
        };

        series.instanceData.push(instance);
      }
    },
    async seriesLevelQido(study) {
      //SERIES LEVEL QIDO for all series in study
      const config = useConfigStore();

      let self = this;

      var tempStudyUID = study.studyUID;
      var seriesQIDO = `${config.qidoRoot}/studies/${tempStudyUID}/series?includefield=00080021&includefield=00201209&includefield=0008103E&includefield=0020000E&includefield=00200011&includefield=00080060`;

      await axios.get(seriesQIDO).then(function (res) {
        var data = res.data;
        data.forEach((ser) => {
          var series = {
            initialized: false,
            studyDescription: study.studyDescription,
            seriesNumber: self.GetAttribute(ser["00200011"]),
            seriesUID: self.GetAttribute(ser["0020000E"]),
            seriesRelatedInstances: self.GetAttribute(ser["00201209"]),
            displayImageCount: 0,
            seriesDescription: self.GetAttribute(ser["0008103E"]),
            seriesDate: self.GetAttribute(ser["00080021"]),
            studyDate: study.studyDate,
            modality: self.GetAttribute(ser["00080060"]),
            imageIds: [],
            instanceData: [],
          };

          study.series.push(series);
          if (!study.prior) {
            self.mainSeriesUIDs.push(series.seriesUID);
          } else {
            self.priorSeriesUIDs.push(series.seriesUID);
          }
        });
      });

      //sort series by series number
      study.series.sort((a, b) => a.seriesNumber - b.seriesNumber);

      return study;
    },
    async relatedQido(apiRoute, mainStudyUID) {
      let self = this;
      await axios.get(apiRoute).then(function (res) {
        var data = res.data;
        if (res.data.length > 0) {
          data.forEach((stud) => {
            var relatedStudy = {
              patientName: self.GetAttribute(stud["00100010"]),
              patientID: self.GetAttribute(stud["00100020"]),
              patientAge: self.GetAttribute(stud["00101010"]),
              DOB: self.GetAttribute(stud["00100030"]),
              studyUID: self.GetAttribute(stud["0020000D"]),
              studyDescription: self.GetAttribute(stud["00081030"]),
              studyDate: self.GetAttribute(stud["00080020"]),
              studyTime: self.GetAttribute(stud["00080030"]),
              modalitiesInStudy: self.GetAttribute(stud["00080061"]),
              institutionName: self.GetAttribute(stud["00080080"]),
              accessionNumber: self.GetAttribute(stud["00080050"]),
              series: [],
              prior: true,
            };

            //add everything but the study we have opened
            if (relatedStudy.studyUID != mainStudyUID) {
              let originalStudyDate = formatDicomDate(
                self.studiesLoaded[0].studyDate,
              );
              let priorStudyDate = formatDicomDate(relatedStudy.studyDate);

              const wasPrior = compareDates(priorStudyDate, originalStudyDate);

              if (wasPrior) {
                let alreadyFound = self.relatedStudies.find(
                  (x) => x.studyUID === relatedStudy.studyUID,
                );

                if (!alreadyFound) {
                  self.relatedStudies.push(relatedStudy);
                }
              }
            }
          });
        }
      });
    },
    async loadStudyFromServer(studyUID, type) {
      const config = useConfigStore();
      let self = this;

      //tell the user what is happening
      var element = document.getElementById("viewer-placeholder-text");
      element.innerHTML = "Loading metadata";
      var dots = document.getElementById("dots");
      dots.style.display = "block";

      this.type = type;
      if (type === "web_api") {
        var ApiURL = `${config.webApiGetStudy}/api/Dicom/getStudy?studyUID=${studyUID}`;
        await axios
          .get(ApiURL)
          .then(async function (res) {
            var result = res.data;

            let newStudy = {
              studyUID: result.studyUID,
              patientID: result.patientID,
              series: result.seriesList.map((s) => ({
                seriesUID: s.seriesUID,
                modality: s.modality,
                seriesNumber: s.seriesNumber,
                imageIds: [],
                instances: [],
              })),
            };

            self.studiesLoaded.push(newStudy);
            self.currentStudy = newStudy.studyUID;
            for (let i = 0; i < result.seriesList.length; i++) {
              let seriesItem = result.seriesList[i];
              let targetSeries = newStudy.series.find(
                (s) => s.seriesUID === seriesItem.seriesUID,
              );
              if (!targetSeries) continue;
              for (let j = 0; j < seriesItem.instances.length; j++) {
                let filePath =
                  `${config.webApiGetStudy}/api/Dicom/GetImage?filePath=` +
                  seriesItem.instances[j].filePath;
                try {
                  const response = await fetch(filePath);
                  if (!response.ok) {
                    throw new Error(filePath);
                  }
                  const blob = await response.blob();
                  const rootImageId =
                    cornerstoneDICOMImageLoader.wadouri.fileManager.add(blob);
                  const image =
                    await cornerstoneDICOMImageLoader.wadouri.loadImage(
                      rootImageId,
                    ).promise;
                  const dataSet = image.data;
                  const numberOfFrames = dataSet.intString("x00280008");
                  const frames = numberOfFrames ? parseInt(numberOfFrames) : 1;
                  let instanceImageIds = [];
                  if (frames > 1) {
                    for (let frame = 1; frame <= frames; frame++) {
                      const frameImageId = `${rootImageId}?frame=${frame}`;
                      instanceImageIds.push(frameImageId);
                      targetSeries.imageIds.push(frameImageId);
                    }
                  } else {
                    targetSeries.imageIds.push(rootImageId);
                    instanceImageIds = [rootImageId];
                  }

                  if (
                    !dataSet ||
                    !dataSet.elements ||
                    !dataSet.elements["x7fe00010"]
                  ) {
                    continue;
                  } else {
                    instanceImageIds.forEach((instanceImageId) => {
                      populateMetadataFromImageData(image, instanceImageId);
                    });
                    // WADORSHeaderProvider.addInstance(
                    //   instanceImageIds[0],
                    //   dataSet
                    // );
                    targetSeries.instances.push(image);
                  }
                } catch (err) {
                  console.error(err);
                }
              }
            }
          })
          .then(async () => {
            await self.getAndSetAllDefaults();
            await self.setViewportLayoutAndToolsForStudy();
          })
          .then(() => {
            self.preDataLoaded = true;
          })
          .catch(function (error) {
            console.error(error);
          });
      } else {
        //STUDY LEVEL QIDO
        try {
          var studyQido = `${config.qidoRoot}/studies?0020000D=${studyUID}&includefield=00101010&includefield=00100030&includefield=00081030&includefield=00100010&includefield=00100020&includefield=00080061&includefield=00080020&includefield=00080080&includefield=00080030`;
          await axios
            .get(studyQido)
            .then(async function (res) {
              var data = res.data;

              if (!data || data.length == 0) {
                throw new Error("No Study Found");
              }

              var study = {
                patientName: self.GetAttribute(data[0]["00100010"]),
                patientID: self.GetAttribute(data[0]["00100020"]),
                patientAge: self.GetAttribute(data[0]["00101010"]),
                DOB: self.GetAttribute(data[0]["00100030"]),
                studyUID: self.GetAttribute(data[0]["0020000D"]),
                studyID: self.GetAttribute(data[0]["00200010"]),
                studyDescription: self.GetAttribute(data[0]["00081030"]),
                studyDate: self.GetAttribute(data[0]["00080020"]),
                studyTime: self.GetAttribute(data[0]["00080030"]),
                modalitiesInStudy: self.GetAttribute(data[0]["00080061"]),
                institutionName: self.GetAttribute(data[0]["00080080"]),
                patientSex: self.GetAttribute(data[0]["00100040"]),
                referringPhysicianName: self.GetAttribute(data[0]["00080090"]),
                series: [],
                prior: false,
              };
              study = await self.seriesLevelQido(study);

              const promises = study.series.map((series) =>
                self.instanceLevelQido(series, study.studyUID),
              );
              await Promise.all(promises);

              let seriesModalities = study.series.map((x) => x.modality);

              let modality;

              /**
               * cycle through ignoring "DOC" modality until genuine modality found
               * if there is only DOC modality then use DOC layout
               */
              for (let i = 0; i < seriesModalities.length; i++) {
                modality = seriesModalities[i];
                let bool =
                  !(modality === "DOC") &&
                  !(modality === "SR") &&
                  !(modality === "DUMMY");
                if (bool) {
                  study.modalitiesInStudy = modality;

                  // break from loop
                  i = seriesModalities.length;
                }
              }

              let tempStudy = study;

              study.numberOfPriors = await getNumberOfPriors(study);

              self.studiesLoaded.push(tempStudy);
            })
            .then(async function (e) {
              let priors = config.showRelatedStudies;

              //related studies
              if (priors) {
                config.getPriorDefaults();

                let studiesLoaded = self.studiesLoaded.filter(
                  (x) => x.studyUID == studyUID,
                )[0];

                let patientID = studiesLoaded.patientID; //using the patient id of the primary study
                let patientDOB = studiesLoaded.DOB;
                let patientName = studiesLoaded.patientName;

                // PATIENT NAME MATCHES BY FIRST AND LAST SO THIS WORKS
                const matchingPatientName = patientName.replace(" ", "^");
                patientID = patientID.replace(/#/, "%23");

                const matchByPatientIDIncludeFields =
                  "&includefield=00100030&includefield=00100010";
                const matchByPatientNameAndDOBIncludeFields =
                  "&includefield=00100020";
                const studyLevelIncludeFields =
                  "&includefield=00080080&includefield=0020000D&includefield=00080061&includefield=00080020&includefield=00080030&includefield=00081030";

                let patientIDQido = `${config.qidoRoot}/studies?fuzzymatching=true&00100020=${patientID}${matchByPatientIDIncludeFields}${studyLevelIncludeFields}`;
                let patientNameDOBQido = `${config.qidoRoot}/studies?fuzzymatching=true&00100010=${matchingPatientName}&00100030=${patientDOB}${matchByPatientNameAndDOBIncludeFields}${studyLevelIncludeFields}`;

                let relatedQIDO;
                let loadAll = false;
                let loadNone = false;

                let priorDefaults = config.loadPriorsBy;

                if (
                  priorDefaults.loadPriorsByID &&
                  !priorDefaults.loadPriorsByNameDOB
                ) {
                  relatedQIDO = patientIDQido;
                }

                if (
                  priorDefaults.loadPriorsByNameDOB &&
                  !priorDefaults.loadPriorsByID
                ) {
                  relatedQIDO = patientNameDOBQido;
                }

                if (
                  priorDefaults.loadPriorsByID &&
                  priorDefaults.loadPriorsByNameDOB
                ) {
                  loadAll = true;
                  relatedQIDO = patientIDQido;
                }

                if (priorDefaults.dontLoadPriors) loadNone = true;

                if (!loadNone) {
                  if (!relatedQIDO) relatedQIDO = patientIDQido;

                  // GET PRIORS
                  await self.relatedQido(relatedQIDO, studyUID);

                  if (loadAll) {
                    // load priors by id, name and dob
                    await self.relatedQido(patientNameDOBQido, studyUID);
                  }

                  self.relatedStudies.sort((a, b) => b.studyDate - a.studyDate);

                  for (let i = 0; i < self.relatedStudies.length; i++) {
                    let rel = self.relatedStudies[i];
                    rel = await self.seriesLevelQido(rel);
                    for (let j = 0; j < rel.series.length; j++) {
                      await self.instanceLevelQido(rel.series[j], rel.studyUID);
                    }
                    self.studiesLoaded.push(rel);
                  }
                }
              }
              self.currentStudy = studyUID;
            })
            .then(async function (e) {
              // default tools, layouts and thumbnails
              await self.getAndSetAllDefaults();
              await self.setViewportLayoutAndToolsForStudy();
            })
            .then(() => {
              self.preDataLoaded = true;
            });
        } catch (error) {
          const alert = useAlertStore();
          alert.showFailureAlert({ text: error.message });
          element.innerHTML = "No Images Loaded";
          dots.style.display = "none";
        }
      }
    },
    async setViewportLayoutAndToolsForStudy() {
      const study = this.studiesLoaded.find(
        (x) => x.studyUID === this.currentStudy,
      );
      if (!study) return;

      const seriesModalities = study.series.map((x) => x.modality);
      let modality = "DOC";

      for (let i = 0; i < seriesModalities.length; i++) {
        const current = seriesModalities[i];
        const isRealModality =
          current !== "DOC" && current !== "SR" && current !== "DUMMY";

        if (isRealModality) {
          modality = current;
          study.modalitiesInStudy = modality;
          break;
        }
      }

      let defaults = JSON.parse(localStorage.getItem("defaultLayouts"));
      let layoutForModality = null;

      if (defaults) {
        layoutForModality = defaults.find((x) => x.modality === modality);
      }

      let layout;

      if (!layoutForModality) {
        const newLayout = {
          modality,
          row: 1,
          col: 1,
        };
        this.defaults.push(newLayout);
        layout = `${newLayout.row}x${newLayout.col}`;
      } else {
        layout = `${layoutForModality.row}x${layoutForModality.col}`;
      }

      if (layout) {
        this.SetLayout(layout);
      }

      const toolsStore = useToolsStore();

      const fallbackTools = {
        0: "WindowLevel",
        1: "Pan",
        2: "Zoom",
      };

      const activeTools = toolsStore?.activeMouseTools || fallbackTools;

      let toolsDefaults =
        JSON.parse(localStorage.getItem("defaultTools")) || [];
      const toolDefault = toolsDefaults.filter((x) => x.modality === modality);

      let lm = activeTools[0] || fallbackTools[0];
      let mm = activeTools[1] || fallbackTools[1];
      let rm = activeTools[2] || fallbackTools[2];

      if (toolDefault.length > 0) {
        for (const item of toolDefault) {
          switch (item.control) {
            case 1:
              lm = item.tool || lm;
              break;
            case 2:
              rm = item.tool || rm;
              break;
            case 4:
              mm = item.tool || mm;
              break;
          }
        }
      } else {
        toolsDefaults.push(
          { modality, control: 1, tool: lm },
          { modality, control: 2, tool: rm },
          { modality, control: 4, tool: mm },
        );
        localStorage.setItem("defaultTools", JSON.stringify(toolsDefaults));
      }

      toolsStore.activeMouseTools = {
        0: lm,
        1: mm,
        2: rm,
      };

      if (typeof toolsStore.ApplyStackInputBindings === "function") {
        toolsStore.ApplyStackInputBindings();
      }
    },

    async getAndSetAllDefaults() {
      const UIstore = useUIStore();
      const config = useConfigStore();

      let thumbnailDefaults = await JSON.parse(
        localStorage.getItem("defaultThumb"),
      );

      if (thumbnailDefaults != null) {
        UIstore.thumbnailHSize = thumbnailDefaults["thumbnailHSize"];

        UIstore.thumbnailVSize = thumbnailDefaults["thumbnailVSize"];

        UIstore.thumbnailBottom = thumbnailDefaults["thumbnailHBottom"];

        UIstore.thumbnailLeft = thumbnailDefaults["thumbnailVleft"];
      }
      config.getPriorDefaults();
    },
    GetAllDefaults(query) {
      return axios.get(query);
    },
    GetNoneImageDataSopClassName(sopclassuid) {
      const sopClassName = this.nonImagingSopClasses.get(sopclassuid);
      return sopClassName;
    },
    ProcessNonImagingData(data) {
      const sopClassName = this.nonImagingSopClasses.get(
        data["00080016"].Value[0],
      );
      var resultHTML = "";

      //   var lineHTML = `<div class='sr-attribute'>${tagName} : ${attrValue}</div>`;
      //   TopLevelattributesHTML += lineHTML;
      // });

      //Assemble the SR html
      resultHTML +=
        `<div class='sr-data'> ` +
        `<div class='sr-data-title'>` +
        sopClassName +
        `</div><hr style="width: 50%; border: 1px solid #9b7a3c;"/>` +
        `<br/>` +
        `</div>`;
      return resultHTML;
    },
    GetAttribute(attribute) {
      if (attribute && attribute.Value) {
        if (attribute.Value[0].Alphabetic) return attribute.Value[0].Alphabetic;
        else return attribute.Value[0];
      } else return "";
    },
    handleMultiStackScroll(event) {
      let visibleMultiSelected =
        document.getElementsByClassName("multi-selected");
      if (visibleMultiSelected.length == 0) {
        return;
      }
      //-1 = up : 1 = down
      const scrollDirection = event.deltaY < 0 ? -1 : 1;
      ////Update image indexes for multi selected viewports
      this.multiSelectedViewports.forEach((viewport) => {
        viewport.scroll(scrollDirection);
      });
    },
    removeMultiSelected(viewport) {
      const index = this.multiSelectedViewports.indexOf(viewport);
      if (index !== -1) {
        this.multiSelectedViewports.splice(index, 1);
      }
    },
  },
});

// Function to parse a value from byteArray based on VR
const textDecoder = new TextDecoder("iso-8859-1");

function parseValue(byteArray, byteArrayParser, element) {
  const { vr, dataOffset, length } = element;

  // Safety check to prevent reading past buffer
  if (dataOffset + length > byteArray.length) return null;

  switch (vr) {
    case "UI":
    case "SH":
    case "LO":
    case "ST":
    case "LT":
    case "CS":
    case "DA":
    case "TM": {
      const subArray = byteArray.subarray(dataOffset, dataOffset + length);
      const str = textDecoder.decode(subArray).trim();
      return str.replace(/\0/g, "").split("\\");
    }
    case "US":
      return byteArrayParser.readUint16(byteArray, dataOffset);
    case "UL":
      return byteArrayParser.readUint32(byteArray, dataOffset);
    case "SS":
      return byteArrayParser.readInt16(byteArray, dataOffset);
    case "SL":
      return byteArrayParser.readInt32(byteArray, dataOffset);
    case "FL":
      return byteArrayParser.readFloat(byteArray, dataOffset);
    case "FD":
      return byteArrayParser.readDouble(byteArray, dataOffset);

    case "OB":
    case "OW":
      // .subarray creates a view (pointer) rather than copying memory like .slice
      return byteArray.subarray(dataOffset, dataOffset + length);

    case "SQ":
      if (!element.items || !Array.isArray(element.items)) return [];

      return element.items.map((item) => {
        const parsedItem = {};
        for (const tag in item.dataSet.elements) {
          const subElement = item.dataSet.elements[tag];
          const cleanTag = tag.replace("x", "").toUpperCase();
          const value = parseValue(
            item.dataSet.byteArray,
            item.dataSet.byteArrayParser,
            subElement,
          );
          if (value !== null) {
            parsedItem[cleanTag] = {
              Value: Array.isArray(value) ? value : [value],
              vr: subElement.vr,
            };
          }
        }
        return parsedItem;
      }); // Return raw items if available

    default:
      console.warn(`Unsupported VR: ${vr} for tag ${element.tag}`);
      return null;
  }
}

// Function to populate metaDataManager from image.data
function populateMetadataFromImageData(image, imageId) {
  const byteArray = image.data.byteArray;
  const elements = image.data.elements;
  const byteArrayParser = image.data.byteArrayParser; // Assuming this exists
  const metadata = {};

  // Process each element
  for (const tag in elements) {
    const element = elements[tag];
    const cleanTag = tag.replace("x", "").toUpperCase(); // e.g., "x0020000d" -> "0020000D"
    const value = parseValue(byteArray, byteArrayParser, element);

    if (value !== null) {
      metadata[cleanTag] = {
        Value: Array.isArray(value) ? value : [value], // Ensure value is an array
        vr: element.vr,
      };
    }
  }

  // Add to metaDataManager
  cornerstoneDICOMImageLoader.wadors.metaDataManager.add(imageId, metadata);
  return metadata;
}
